/** 도장 사용 시 PIN 6자리 입력 모달
 *  - 입력받은 PIN 을 onSubmit(pin) 으로 넘기고 결과(boolean)를 기다린다.
 *  - 실제 검증은 발송 API(SendContractRequest)에서 백엔드가 수행.
 *  - onSubmit 이 false(=PIN 오류/실패)면 모달을 닫지 않고 최신 실패 횟수를 갱신해 표시한다.
 *    (성공 시에만 부모가 모달을 닫고 화면을 이동)
 *  - PIN 미등록 사장님에게는 도장 관리로 안내
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { IconLock } from "./icons";
import { usePinStatus } from "~/services/business/seal";
import { useNavVisibilityStore } from "&/navVisibility";

interface Props {
  /** 인증할 도장 이름(헤더 표시용) */
  sealName?: string;
  onClose: () => void;
  /** PIN 제출 → 발송 시도. 성공 true / 실패(주로 PIN 오류) false 반환. false 면 모달 유지. */
  onSubmit: (pin: string) => Promise<boolean>;
}

export const PinVerifyModal = ({ sealName, onClose, onSubmit }: Props) => {
  const { data: pinStatus, refetch } = usePinStatus();
  const setNavVisible = useNavVisibilityStore((s) => s.setVisible);
  const hasPin = !!pinStatus?.hasPin;
  const failCount = pinStatus?.failCount ?? 0;
  const locked =
    !!pinStatus?.lockedUntil && new Date(pinStatus.lockedUntil) > new Date();

  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // 모바일 하단 시트가 플로팅 nav 와 같은 영역을 쓰므로 인증 중에는 nav 를 숨긴다.
  // 편집기처럼 원래 nav 가 숨겨진 화면에서는 닫힐 때 기존 상태(false)를 그대로 복원한다.
  useEffect(() => {
    const mobileMedia = window.matchMedia("(max-width: 767px)");
    const previousVisible = useNavVisibilityStore.getState().visible;
    const syncNavVisibility = () => {
      setNavVisible(mobileMedia.matches ? false : previousVisible);
    };

    syncNavVisibility();
    mobileMedia.addEventListener("change", syncNavVisibility);

    return () => {
      mobileMedia.removeEventListener("change", syncNavVisibility);
      setNavVisible(previousVisible);
    };
  }, [setNavVisible]);

  const handle = async () => {
    if (submitting) return;
    if (locked)
      return alert("PIN이 잠금 상태입니다. 잠시 후 다시 시도해 주세요");
    if (!/^\d{6}$/.test(pin)) return alert("PIN 6자리를 입력해 주세요");
    setSubmitting(true);
    const ok = await onSubmit(pin);
    setSubmitting(false);
    if (!ok) {
      // PIN 오류 등 실패 — 모달을 닫지 않고 최신 실패 횟수 갱신 + 입력 초기화 후 재시도 유도
      setPin("");
      await refetch();
    }
    // 성공(true)이면 부모(onSubmit)가 모달을 닫고 화면을 이동한다.
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-end md:items-center justify-center"
      onClick={submitting ? undefined : onClose}
    >
      <div
        className="bg-white rounded-t-3xl md:rounded-3xl w-full md:max-w-md p-5 pb-[calc(1.25rem_+_env(safe-area-inset-bottom,0px))] md:pb-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[16px] font-bold mb-1 inline-flex items-center gap-1.5">
          <IconLock className="w-4 h-4 text-[#FF9934]" />
          도장 사용 인증
        </div>
        <div className="text-[12px] text-[#666] mb-4">
          {sealName ? (
            <>
              <b>"{sealName}"</b> 도장 사용을 위해 PIN 6자리를 입력해 주세요
            </>
          ) : (
            "도장 사용을 위해 PIN 6자리를 입력해 주세요"
          )}
        </div>

        {!hasPin && (
          <div className="bg-[#FFF1F1] border border-[#F24747] text-[#F24747] text-[11px] rounded p-2 mb-3">
            PIN이 등록되지 않았어요. 도장 관리에서 먼저 등록해 주세요.
          </div>
        )}

        <input
          inputMode="numeric"
          maxLength={6}
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
          placeholder="● ● ● ● ● ●"
          className="w-full px-3 py-3 text-[18px] tracking-[0.4em] text-center border border-[#E9E9E9] rounded-lg focus:outline-none focus:border-[#FF9934] mb-2"
          autoFocus
        />
        {failCount > 0 && !locked && (
          <div className="text-[11px] text-[#F24747] text-center mb-2">
            {failCount}회 틀렸어요 (5회 실패 시 잠금)
          </div>
        )}
        {locked && (
          <div className="text-[11px] text-[#F24747] text-center mb-2 inline-flex items-center justify-center gap-1 w-full">
            <IconLock className="w-3 h-3" />
            5회 실패 — 잠시 후 다시 시도하세요
          </div>
        )}

        <div className="flex gap-2 mt-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex-1 py-2.5 text-[12px] border border-[#E9E9E9] text-[#666] rounded-full disabled:opacity-50"
          >
            취소
          </button>
          <button
            type="button"
            onClick={handle}
            disabled={!hasPin || locked || submitting}
            className="flex-[2] py-2.5 text-[12px] font-semibold bg-[#FF9934] text-white rounded-full disabled:bg-[#E9E9E9] disabled:text-[#999]"
          >
            {submitting ? "인증 중…" : "인증하고 계속"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
